// v6 - stat cards fix: added PDD and Closed
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiEye, FiEdit2, FiPhone, FiCalendar,
  FiAlertCircle, FiFilter, FiX, FiChevronDown,
  FiRefreshCw, FiTrendingUp, FiLogIn, FiCheckCircle, FiDollarSign, FiUsers,
  FiLock, FiPackage, FiXCircle,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import Layout from '../../components/Layout';
import Pagination from '../../components/Pagination';
import { toast } from 'react-toastify';
import { fmtDate } from '../../utils/dateUtils';
import DateInput from '../../components/DateInput';
import authService from '../../services/authService';
import { useAllUsers } from '../../hooks/useAllUsers';
import { canAccessLeadRecord, getReportScopeUserIds, getReportScopeUsers, isAdminLike } from '../../utils/roleAccess';

const fmtAmount = (n) => n ? '₹' + Number(n).toLocaleString('en-IN') : '—';
const todayStr  = () => new Date().toISOString().split('T')[0];

const STAGE_TO_DISPLAY = {
  FOLLOW_UP:    { label: 'Follow Up'       },
  LOGIN:        { label: 'Login'            },
  SANCTION:     { label: 'Sanction'         },
  DISBURSEMENT: { label: 'Disbursement'    },
  PDD_PENDING: { label: 'PDD Pending'     },
  PDD:          { label: 'PDD'             },
  REVENUE:      { label: 'Revenue'         },
  COMPLETE:     { label: 'Completed'       },
  CLOSED:       { label: 'Closed'          },
};

const getStatusDisplay = (lead) => {
  const stage = lead?.current_stage;
  if (stage === 'COMPLETE') return { label: 'Completed' };
  if (stage === 'CLOSED') {
    const closureStage = lead?.closure_stage;
    if (!closureStage) return { label: 'Completed' };
    return { label: 'Closed (Cancelled)' };
  }
  if (stage && STAGE_TO_DISPLAY[stage]) return STAGE_TO_DISPLAY[stage];
  return { label: 'Follow Up' };
};

const getLeadStageCode = (lead) => lead?.current_stage || 'FOLLOW_UP';

const isPastMPlus1 = (lead) => {
  const stage = getLeadStageCode(lead);
  if (!['PDD', 'REVENUE'].includes(stage)) return false;
  const pddDate = lead?.pdd_clearance_date || lead?.pdd?.clearance_date || null;
  if (!pddDate) return false;
  const pdd = new Date(pddDate);
  if (isNaN(pdd)) return false;
  return new Date() >= new Date(pdd.getFullYear(), pdd.getMonth() + 2, 1);
};

const isPastMonthEndDisbursed = (lead) => {
  if (getLeadStageCode(lead) !== 'DISBURSEMENT') return false;
  const disbDate = lead?.disbursement_date || lead?.last_disbursement_date || lead?.disbursed_date || null;
  if (!disbDate) return false;
  const disb = new Date(disbDate);
  if (isNaN(disb)) return false;
  const now = new Date();
  return (disb.getFullYear() * 12 + disb.getMonth()) < (now.getFullYear() * 12 + now.getMonth());
};

const canEditLead = (lead, userRole) => {
  const stage = getLeadStageCode(lead);
  if (stage === 'CLOSED' || stage === 'COMPLETE') return false;
  if (stage === 'PDD' || stage === 'REVENUE') { const r = (userRole || '').toLowerCase(); return r === 'admin' || r === 'administrator' || r === 'super admin' || r === 'superadmin'; }
  return true;
};

const getEditBlockReason = (lead, userRole) => {
  const stage = getLeadStageCode(lead);
  if (stage === 'COMPLETE') return 'Lead is completed and cannot be edited.';
  if (stage === 'CLOSED') return 'Lead is closed and cannot be edited.';
  if ((stage === 'PDD' || stage === 'REVENUE') && !canEditLead(lead, userRole)) return 'PDD Done leads can only be edited by Admin .';
  return '';
};

const SOURCE_CFG = { BSA: { bg:'#EDE9FE', color:'#6D28D9', border:'#C4B5FD' }, Direct: { bg:'#DBEAFE', color:'#1D4ED8', border:'#BFDBFE' }, DIRECT: { bg:'#DBEAFE', color:'#1D4ED8', border:'#BFDBFE' }, Internal: { bg:'#DCFCE7', color:'#15803D', border:'#BBF7D0' }, INTERNAL: { bg:'#DCFCE7', color:'#15803D', border:'#BBF7D0' } };
const C = { navy:'#0D3B5E', navyL:'#EBF4FF', gold:'#F5A623', goldL:'#FEF3DC', red:'#DC2626', redL:'#FEF2F2', redB:'#FECACA', border:'#E5E7EB', borderL:'#F3F4F6', white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF' };

function StatusBadge({ lead }) {
  const { label } = getStatusDisplay(lead);
  const cfg = { 'Follow Up':{bg:'#FFF7ED',color:'#C2410C',border:'#FED7AA'}, 'Login':{bg:'#EDE9FE',color:'#6D28D9',border:'#C4B5FD'}, 'Sanction':{bg:'#DBEAFE',color:'#1D4ED8',border:'#BFDBFE'}, 'Disbursement':{bg:'#DCFCE7',color:'#15803D',border:'#BBF7D0'}, 'PDD':{bg:'#EBF4FF',color:'#0369A1',border:'#BAE6FD'}, 'Revenue':{bg:'#CCFBF1',color:'#0F766E',border:'#5EEAD4'}, 'Completed':{bg:'#DCFCE7',color:'#15803D',border:'#BBF7D0'}, 'Closed (Cancelled)':{bg:'#FEF2F2',color:'#B91C1C',border:'#FECACA'}, 'Closed':{bg:'#FEF2F2',color:'#B91C1C',border:'#FECACA'} }[label] || { bg:'#F3F4F6',color:'#374151',border:'#E5E7EB' };
  return <span style={{ display:'inline-flex', padding:'3px 10px', borderRadius:999, fontSize:11.5, fontWeight:700, background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`, whiteSpace:'nowrap' }}>{label}</span>;
}

function SourceBadge({ source }) {
  const cfg = SOURCE_CFG[source] || { bg:'#F3F4F6', color:'#374151', border:'#E5E7EB' };
  return <span style={{ display:'inline-flex', padding:'3px 10px', borderRadius:999, fontSize:11.5, fontWeight:600, background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}` }}>{source || '—'}</span>;
}

function StatCard({ label, value, icon, color, active, onClick, loading }) {
  return (
    <div onClick={onClick} style={{ background: active ? color : C.white, borderRadius:12, border:`1.5px solid ${active ? color : C.border}`, padding:'10px 12px', cursor:'pointer', boxShadow: active ? `0 4px 16px ${color}35` : '0 2px 6px rgba(13,59,94,0.06)', transition:'all 0.15s', position:'relative', overflow:'hidden' }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.transform='translateY(-2px)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.transform=''; }}>
      {active && <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'rgba(255,255,255,0.35)' }}/>}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
        <span style={{ fontSize:9.5, fontWeight:700, color: active ? 'rgba(255,255,255,0.75)' : C.t4, textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</span>
        <div style={{ width:22, height:22, borderRadius:6, background: active ? 'rgba(255,255,255,0.18)' : color+'18', display:'flex', alignItems:'center', justifyContent:'center', color: active ? '#fff' : color }}>{icon}</div>
      </div>
      {loading ? <div style={{ height:22, width:'45%', borderRadius:6, background: active ? 'rgba(255,255,255,0.22)' : '#F1F5F9', animation:'shimmer 1.4s infinite' }}/> : <div style={{ fontSize:22, fontWeight:800, color: active ? '#fff' : C.t1, letterSpacing:'-0.05em', lineHeight:1 }}>{value}</div>}
    </div>
  );
}

const Shimmer = ({ h=52, r=8 }) => <div style={{ height:h, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'shimmer 1.4s infinite' }}/>;

function LeadCard({ lead, navigate, overdue, isToday, editOk, onEditClick, userRole }) {
  return (
    <div onClick={() => navigate(`/leads/${lead.id}`)} style={{ background:C.white, borderRadius:12, border:`1.5px solid ${overdue ? C.red : isToday ? C.gold : C.border}`, padding:'14px 16px', cursor:'pointer', boxShadow: overdue ? '0 2px 8px rgba(220,38,38,0.10)' : '0 1px 4px rgba(13,59,94,0.06)' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8, marginBottom:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
            <span style={{ fontFamily:'monospace', fontSize:12, fontWeight:700, color:C.navy }}>{lead.lead_no || `#${lead.id}`}</span>
            {overdue && <span style={{ fontSize:10, fontWeight:800, padding:'1px 7px', borderRadius:999, background:C.redL, color:C.red, border:`1px solid ${C.redB}` }}>OVERDUE</span>}
            {isToday && !overdue && <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:999, background:C.goldL, color:'#92400E', border:'1px solid #FDE68A' }}>TODAY</span>}
          </div>
          <div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>{fmtDate(lead.lead_date)}</div>
        </div>
        <div style={{ display:'flex', gap:6, flexShrink:0 }} onClick={e => e.stopPropagation()}>
          <button title="View" onClick={e => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }} style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.navy, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }} onMouseEnter={e=>{e.currentTarget.style.background=C.navyL;}} onMouseLeave={e=>{e.currentTarget.style.background=C.white;}}><FiEye size={15}/></button>
          <button title={editOk ? 'Edit' : getEditBlockReason(lead, userRole)} onClick={e => onEditClick(e, lead)} style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`, background: editOk ? C.white : '#F3F4F6', color: editOk ? C.t2 : C.t4, cursor: editOk ? 'pointer' : 'not-allowed', display:'inline-flex', alignItems:'center', justifyContent:'center', opacity: editOk ? 1 : 0.6 }} onMouseEnter={e=>{ if(editOk) e.currentTarget.style.background=C.navyL; }} onMouseLeave={e=>{ if(editOk) e.currentTarget.style.background=C.white; }}>{editOk ? <FiEdit2 size={15}/> : <FiLock size={14}/>}</button>
        </div>
      </div>
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:15, fontWeight:700, color:C.t1 }}>{lead.customer_name || '—'}</div>
        {lead.mobile_number && <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:C.t3, marginTop:3 }}><FiPhone size={11}/> {lead.mobile_number}</div>}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 12px', marginBottom:10 }}>
        {lead.product?.name && <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, color:C.t2, overflow:'hidden' }}><FiPackage size={11} style={{ color:C.t4, flexShrink:0 }}/><span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.product.name}</span></div>}
        <div style={{ fontSize:13.5, fontWeight:700, color:C.navy }}>{fmtAmount(lead.loan_amount)}</div>
        <SourceBadge source={lead.lead_source}/>
        <StatusBadge lead={lead}/>
      </div>
      {lead.next_follow_up_date && (
        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', background: overdue ? C.redL : '#F8FAFC', borderRadius:8, border:`1px solid ${overdue ? C.redB : C.borderL}` }}>
          {overdue ? <FiAlertCircle size={12} style={{ color:C.red, flexShrink:0 }}/> : <FiCalendar size={12} style={{ color:C.t4, flexShrink:0 }}/>}
          <span style={{ fontSize:12.5, fontWeight: overdue ? 700 : 500, color: overdue ? C.red : C.t2 }}>Follow-up: {fmtDate(lead.next_follow_up_date)}</span>
        </div>
      )}
    </div>
  );
}

function useWindowWidth() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => { const h = () => setW(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return w;
}

const PAGE_SIZE = 15;

export default function LeadsList() {
  const navigate  = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const vw        = useWindowWidth();
  const isMobile  = vw < 768;
  const isTablet  = vw >= 768 && vw < 1024;

  const currentUser = authService.getUser();
  const { users: allUsers } = useAllUsers();
  const scopedUsers = useMemo(() => getReportScopeUsers(currentUser, allUsers), [currentUser, allUsers]);
  const scopeUserIds = useMemo(() => getReportScopeUserIds(scopedUsers), [scopedUsers]);
  const isAdminCurrentUser = isAdminLike(currentUser);
  const userRole = (() => { try { const u = currentUser || JSON.parse(localStorage.getItem('user') || '{}'); if (u?.role && typeof u.role === 'object') return u.role.name || ''; if (u?.role_name) return u.role_name; if (u?.role && typeof u.role === 'string') return u.role; if (u?.is_superuser || u?.is_staff) return 'Super Admin'; return ''; } catch { return ''; } })();

  const [leads,          setLeads]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [countsLoading,  setCountsLoading]  = useState(true);
  const [total,          setTotal]          = useState(0);
  const [stageCounts,    setStageCounts]    = useState({});
  const [currentPage,    setCurrentPage]    = useState(1);
  const [search,         setSearch]         = useState('');
  const [filterSource,   setFilterSource]   = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterFrom,     setFilterFrom]     = useState('');
  const [filterTo,       setFilterTo]       = useState('');
  const [showDatePanel,  setShowDatePanel]  = useState(false);
  const [activeStatCard, setActiveStatCard] = useState(() => {
    const s = searchParams.get('stage');
    if (!s || s === 'total') return null;
    if (s === 'pipeline') return null;
    const stageMap = { follow_up:'followup', login:'login', sanction:'sanction', disbursement:'disbursed', pdd:'pdd', revenue:'revenue', completed:'completed', closed_cancelled:'closed', closed:'closed' };
    return stageMap[s.toLowerCase()] || null;
  });
  const [urlPipeline, setUrlPipeline] = useState(() => searchParams.get('stage') === 'pipeline');
  const [editBlockModal, setEditBlockModal] = useState(null);

  // Apply URL filter params when searchParams change
  useEffect(() => {
    const stage = searchParams.get('stage');
    const filter = searchParams.get('filter');
    const stageToTab = { follow_up:'Follow Up', login:'Login', sanction:'Sanction', disbursement:'Disbursement', pdd:'PDD', revenue:'Revenue', completed:'Completed', closed_cancelled:'Closed', closed:'Closed' };
    if (stage && stage !== 'pipeline') {
      const tabLabel = stageToTab[stage.toLowerCase()];
      if (tabLabel) setFilterStatus(tabLabel);
      const stageMap = { follow_up:'followup', login:'login', sanction:'sanction', disbursement:'disbursed', pdd:'pdd', revenue:'revenue', completed:'completed', closed_cancelled:'closed', closed:'closed' };
      setActiveStatCard(stageMap[stage.toLowerCase()] || null);
      setUrlPipeline(false);
    } else if (stage === 'pipeline') {
      setUrlPipeline(true);
      setActiveStatCard(null);
      setFilterStatus('');
    } else if (filter === 'overdue') {
      setActiveStatCard('followup');
      setUrlPipeline(false);
    } else if (filter === 'today_followup') {
      setActiveStatCard('followup');
      setUrlPipeline(false);
    } else {
      setActiveStatCard(null);
      setUrlPipeline(false);
    }
  }, [searchParams]); // eslint-disable-line

  const searchRef       = useRef('');
  const filterSourceRef = useRef('');
  const filterFromRef   = useRef('');
  const filterToRef     = useRef('');
  useEffect(() => { searchRef.current       = search;       }, [search]);
  useEffect(() => { filterSourceRef.current = filterSource; }, [filterSource]);
  useEffect(() => { filterFromRef.current   = filterFrom;   }, [filterFrom]);
  useEffect(() => { filterToRef.current     = filterTo;     }, [filterTo]);

  // Sync URL params when activeStatCard changes
  useEffect(() => {
    const stage = searchParams.get('stage');
    const filter = searchParams.get('filter');
    if (activeStatCard && !stage && !filter) {
      // User clicked a stat card locally — update URL
      const revMap = { followup:'FOLLOW_UP', login:'LOGIN', sanction:'SANCTION', disbursed:'DISBURSEMENT', pdd:'PDD', revenue:'REVENUE', completed:'COMPLETED', closed:'CLOSED_CANCELLED' };
      if (revMap[activeStatCard]) {
        setSearchParams({ stage: revMap[activeStatCard] }, { replace: true });
      }
    }
  }, [activeStatCard]); // eslint-disable-line

  const baseParams = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (filterSource) {
      const sourceMap = { Direct:'DIRECT', Internal:'INTERNAL' };
      params.lead_source = sourceMap[filterSource] || filterSource;
    }
    if (filterFrom) params.lead_date_after = filterFrom;
    if (filterTo) params.lead_date_before = filterTo;
    return params;
  }, [search, filterSource, filterFrom, filterTo]);

  const getSelectedStage = useCallback(() => {
    const cardMap = { followup:'FOLLOW_UP', login:'LOGIN', sanction:'SANCTION', disbursed:'DISBURSEMENT', pdd:'PDD', revenue:'REVENUE', completed:'COMPLETED', closed:'CLOSED_CANCELLED' };
    if (activeStatCard && activeStatCard !== 'total') return cardMap[activeStatCard] || '';
    const statusMap = { 'Follow Up':'FOLLOW_UP', 'Login':'LOGIN', 'Sanction':'SANCTION', 'Disbursement':'DISBURSEMENT', 'PDD':'PDD', 'Revenue':'REVENUE', 'Completed':'COMPLETED', 'Closed':'CLOSED_CANCELLED' };
    return statusMap[filterStatus] || '';
  }, [activeStatCard, filterStatus]);

  const fetchLeadCounts = useCallback(async () => {
    setCountsLoading(true);
    try {
      if (!isAdminCurrentUser) {
        const all = [];
        let page = 1;
        while (page <= 50) {
          const res = await api.getLeads({ ...baseParams(), page, page_size: 100 });
          const data = res?.data;
          const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
          all.push(...results);
          if (!data?.next || results.length === 0) break;
          page += 1;
        }
        const scoped = all.filter((lead) => canAccessLeadRecord(lead, currentUser, scopeUserIds));
        const counts = {
          total: scoped.length,
          followup: 0,
          login: 0,
          sanction: 0,
          disbursed: 0,
          pdd: 0,
          revenue: 0,
          completed: 0,
          closed: 0,
        };
        scoped.forEach((lead) => {
          const stage = getLeadStageCode(lead);
          if (stage === 'FOLLOW_UP') counts.followup += 1;
          else if (stage === 'LOGIN') counts.login += 1;
          else if (stage === 'SANCTION') counts.sanction += 1;
          else if (stage === 'DISBURSEMENT') counts.disbursed += 1;
          else if (stage === 'PDD') counts.pdd += 1;
          else if (stage === 'REVENUE') counts.revenue += 1;
          else if (getStatusDisplay(lead).label === 'Completed') counts.completed += 1;
          else if (getStatusDisplay(lead).label === 'Closed (Cancelled)') counts.closed += 1;
        });
        setStageCounts(counts);
        return;
      }

      const stages = {
        followup:'FOLLOW_UP',
        login:'LOGIN',
        sanction:'SANCTION',
        disbursed:'DISBURSEMENT',
        pdd:'PDD',
        revenue:'REVENUE',
        completed:'COMPLETED',
        closed:'CLOSED_CANCELLED',
      };
      const base = baseParams();
      const totalRes = await api.getLeads({ ...base, page:1, size:1 });
      const entries = await Promise.all(Object.entries(stages).map(async ([key, stage]) => {
        const res = await api.getLeads({ ...base, page:1, size:1, stage });
        return [key, res?.data?.count || 0];
      }));
      setStageCounts({ total: totalRes?.data?.count || 0, ...Object.fromEntries(entries) });
    } catch {
      setStageCounts({});
    } finally {
      setCountsLoading(false);
    }
  }, [baseParams, currentUser, isAdminCurrentUser, scopeUserIds]);

  const fetchLeads = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      if (!isAdminCurrentUser) {
        const params = { ...baseParams(), page_size: 100 };
        const selectedStage = getSelectedStage();
        if (selectedStage) params.stage = selectedStage;

        const all = [];
        let page = 1;
        while (page <= 50) {
          const res = await api.getLeads({ ...params, page });
          const data = res?.data;
          const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
          all.push(...results);
          if (!data?.next || results.length === 0) break;
          page += 1;
        }

        const scoped = all.filter((lead) => canAccessLeadRecord(lead, currentUser, scopeUserIds));
        const start = (pg - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        setLeads(scoped.slice(start, end));
        setTotal(scoped.length);
        setCurrentPage(pg);
        return;
      }

      const params = { ...baseParams(), page: pg, page_size: PAGE_SIZE };
      const selectedStage = getSelectedStage();
      if (selectedStage) params.stage = selectedStage;
      
      const res = await api.getLeads(params);
      const data = res?.data;
      const arr = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      
      setLeads(arr);
      setTotal(data?.count || arr.length);
      setCurrentPage(pg);
    } catch (err) { 
      console.error(err); 
      toast.error('Failed to load leads'); 
      setLeads([]); 
    }
    finally { 
      setLoading(false); 
    }
  }, [baseParams, currentUser, getSelectedStage, isAdminCurrentUser, scopeUserIds]); // eslint-disable-line

  useEffect(() => { fetchLeads(1); fetchLeadCounts(); }, [search, filterSource, filterFrom, filterTo, activeStatCard, filterStatus]); // eslint-disable-line

  const FILTER_TO_STAGE = { 'Follow Up':'FOLLOW_UP', 'Login':'LOGIN', 'Sanction':'SANCTION', 'Disbursement':'DISBURSEMENT', 'PDD':'PDD', 'Revenue':'REVENUE', 'Completed':'COMPLETED', 'Closed':'CLOSED_CANCELLED' };

  const br03Filtered   = isAdminCurrentUser ? leads : leads.filter(l => !isPastMPlus1(l) && !isPastMonthEndDisbursed(l));
  const statusFiltered = br03Filtered.filter(l => {
    if (!filterStatus || filterStatus === 'All') return true;
    const selected = FILTER_TO_STAGE[filterStatus];
    if (selected === 'COMPLETED') return getStatusDisplay(l).label === 'Completed';
    if (selected === 'CLOSED_CANCELLED') return getStatusDisplay(l).label === 'Closed (Cancelled)';
    return getLeadStageCode(l) === selected;
  });
  const displayLeads   = statusFiltered.filter(l => {
    if (urlPipeline) return ['LOGIN','SANCTION','DISBURSEMENT','PDD','REVENUE'].includes(getLeadStageCode(l));
    if (!activeStatCard || activeStatCard === 'total') return true;
    const s = getLeadStageCode(l);
    if (activeStatCard === 'followup')  return s === 'FOLLOW_UP';
    if (activeStatCard === 'login')     return s === 'LOGIN';
    if (activeStatCard === 'sanction')  return s === 'SANCTION';
    if (activeStatCard === 'disbursed') return s === 'DISBURSEMENT';
    if (activeStatCard === 'pdd')       return s === 'PDD';
    if (activeStatCard === 'revenue')   return s === 'REVENUE';
    if (activeStatCard === 'completed') return getStatusDisplay(l).label === 'Completed';
    if (activeStatCard === 'closed')    return getStatusDisplay(l).label === 'Closed (Cancelled)';
    return true;
  });

  const clientFilterActive = !!(filterStatus || (activeStatCard && activeStatCard !== 'total'));
  const showPagination     = total > PAGE_SIZE;
  const handlePageChange = (pg) => { fetchLeads(pg); };

  const followUpCount  = stageCounts.followup || 0;
  const loginCount     = stageCounts.login || 0;
  const sanctionCount  = stageCounts.sanction || 0;
  const disbursedCount = stageCounts.disbursed || 0;
  const pddCount       = stageCounts.pdd || 0;
  const revenueCount   = stageCounts.revenue || 0;
  const completedCount = stageCounts.completed || 0;
  const closedCount    = stageCounts.closed || 0;

  const statCards = [
    { key:'total',    label:'Total',     value:stageCounts.total ?? total, icon:<FiUsers size={12}/>, color:C.navy },
    { key:'followup', label:'Follow Up', value:followUpCount,  icon:<FiTrendingUp size={12}/>,  color:'#EA580C' },
    { key:'login',    label:'Login',     value:loginCount,     icon:<FiLogIn size={12}/>,       color:'#7C3AED' },
    { key:'sanction', label:'Sanction',  value:sanctionCount,  icon:<FiCheckCircle size={12}/>, color:'#0284C7' },
    { key:'disbursed',label:'Disbursed', value:disbursedCount, icon:<FiDollarSign size={12}/>,  color:'#16A34A' },
    { key:'pdd',      label:'PDD',       value:pddCount,       icon:<FiCheckCircle size={12}/>, color:'#0369A1' },
    { key:'revenue',  label:'Revenue',   value:revenueCount,   icon:<FiDollarSign size={12}/>,  color:'#0F766E' },
    { key:'completed',label:'Completed', value:completedCount, icon:<FiCheckCircle size={12}/>, color:'#15803D' },
    { key:'closed',   label:'Closed',    value:closedCount,    icon:<FiXCircle size={12}/>,     color:'#B91C1C' },
  ];

  const isOverdue    = (l) => getLeadStageCode(l) === 'FOLLOW_UP' && l.next_follow_up_date && l.next_follow_up_date < todayStr();
  const overdueCount = leads.filter(isOverdue).length;
  const hasFilters   = !!(search || filterSource || filterStatus || filterFrom || filterTo || activeStatCard);
  const clearFilters   = () => { setSearch(''); setFilterSource(''); setFilterStatus(''); setFilterFrom(''); setFilterTo(''); setShowDatePanel(false); setActiveStatCard(null); setUrlPipeline(false); setSearchParams({}, { replace: true }); };
  const handleEditClick = (e, lead) => { e.stopPropagation(); const r = getEditBlockReason(lead, userRole); if (r) { setEditBlockModal({ lead, reason:r }); return; } navigate(`/leads/${lead.id}/edit`); };

  return (
    <>
      <style>{`@keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}} @keyframes spin{to{transform:rotate(360deg)}} @keyframes slideDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <Layout pageTitle="Lead Management">
        <div style={{ display:'flex', flexDirection:'column', gap:12, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

          {/* BANNER */}
          <div style={{ borderRadius:14, padding: isMobile ? '14px 16px' : '20px 28px', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:700, letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:5 }}>Management</div>
              <h2 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:800, color:'#fff', letterSpacing:'-0.03em' }}>Lead Management</h2>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:13, color:'rgba(255,255,255,0.50)' }}>{total.toLocaleString()} lead{total !== 1 ? 's' : ''}</span>
                {overdueCount > 0 && <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 11px', borderRadius:999, fontSize:12, fontWeight:700, background:'rgba(220,38,38,0.22)', border:'1px solid rgba(220,38,38,0.40)', color:'#FCA5A5' }}><FiAlertCircle size={11}/> {overdueCount} overdue</span>}
              </div>
            </div>
            <div style={{ display:'flex', gap:9, alignItems:'center' }}>
              <button onClick={() => fetchLeads(currentPage)} style={{ width:38, height:38, borderRadius:9, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'rgba(255,255,255,0.70)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }} onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.20)';}} onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.11)';}}>
                <FiRefreshCw size={14}/>
              </button>
              <button onClick={() => navigate('/leads/create')} style={{ display:'inline-flex', alignItems:'center', gap:7, padding: isMobile ? '8px 14px' : '9px 20px', borderRadius:10, background:'#F5A623', color:'#0D1B2A', border:'none', fontWeight:800, fontSize: isMobile ? 13 : 13.5, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 14px rgba(245,166,35,0.45)' }} onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.background='#FBBF4A';}} onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.background='#F5A623';}}>
                <FiPlus size={16}/> {isMobile ? 'New' : 'New Lead'}
              </button>
            </div>
          </div>

          {/* STAT CARDS — 8 cards: Total, Follow Up, Login, Sanction, Disbursed, PDD, Revenue, Closed */}
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(3,1fr)' : isTablet ? 'repeat(3,1fr)' : 'repeat(9,1fr)', gap: isMobile ? 6 : 10 }}>
            {statCards.map(card => (
              <StatCard key={card.key} {...card} loading={loading || countsLoading}
                active={activeStatCard === card.key || (!activeStatCard && card.key === 'total')}
                onClick={() => { const v = card.key === 'total' ? null : (activeStatCard === card.key ? null : card.key); setActiveStatCard(v); setUrlPipeline(false); setFilterStatus(''); if (v) { const revMap = { followup:'FOLLOW_UP', login:'LOGIN', sanction:'SANCTION', disbursed:'DISBURSEMENT', pdd:'PDD', revenue:'REVENUE', completed:'COMPLETED', closed:'CLOSED_CANCELLED' }; setSearchParams({ stage: revMap[v] }, { replace: true }); } else { setSearchParams({}, { replace: true }); } }}
              />
            ))}
          </div>

          {/* FILTER BAR */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', padding:'10px 14px', borderBottom:`1px solid ${C.borderL}` }}>
              <div style={{ position:'relative', flex:1, minWidth: isMobile ? 0 : 200, maxWidth: isMobile ? '100%' : 340, width: isMobile ? '100%' : 'auto' }}>
                <FiSearch size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
                <input type="text" placeholder="Search name, mobile, lead no..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width:'100%', height:36, border:`1.5px solid ${C.border}`, borderRadius:9, fontSize:13, fontFamily:'inherit', outline:'none', background:'#F9FAFB', color:C.t1, paddingLeft:34, paddingRight:12, boxSizing:'border-box' }}
                  onFocus={e=>{e.target.style.borderColor=C.navy;e.target.style.background='#fff';}} onBlur={e=>{e.target.style.borderColor=C.border;e.target.style.background='#F9FAFB';}}/>
              </div>
              {!isMobile && <div style={{ width:1, height:22, background:C.border }}/>}
              <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center' }}>
                {!isMobile && <span style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em' }}>Source:</span>}
                {[{val:'',label:'All',ab:C.navy,abo:C.navy,ac:'#fff',ib:'#F9FAFB',ic:C.t3},{val:'BSA',label:'BSA',ab:'#EDE9FE',abo:'#7C3AED',ac:'#6D28D9',ib:'#F9FAFB',ic:C.t3},{val:'Direct',label:'Direct',ab:'#DBEAFE',abo:'#1D4ED8',ac:'#1D4ED8',ib:'#F9FAFB',ic:C.t3},{val:'Internal',label:'Internal',ab:'#DCFCE7',abo:'#16A34A',ac:'#15803D',ib:'#F9FAFB',ic:C.t3}].map(src => {
                  const a = filterSource === src.val;
                  return <button key={src.val} onClick={() => setFilterSource(src.val)} style={{ padding:'4px 11px', borderRadius:20, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:`1.5px solid ${a ? src.abo : C.border}`, background: a ? src.ab : src.ib, color: a ? src.ac : src.ic }}>{src.label}</button>;
                })}
              </div>
              <div style={{ flex:1 }}/>
              <button onClick={() => setShowDatePanel(p => !p)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:9, fontSize:12.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:`1.5px solid ${(filterFrom||filterTo) ? '#0284C7' : C.border}`, background:(filterFrom||filterTo) ? '#DBEAFE' : '#F9FAFB', color:(filterFrom||filterTo) ? '#0284C7' : C.t3 }}>
                <FiFilter size={12}/>{!isMobile && ' Date Range'}<FiChevronDown size={11} style={{ transform:showDatePanel ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }}/>
              </button>
              {hasFilters && <button onClick={clearFilters} style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:9, fontSize:12.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:'1.5px solid #FECACA', background:'#FEF2F2', color:'#DC2626' }}><FiX size={11}/>{!isMobile && ' Clear'}</button>}
            </div>
            {showDatePanel && (
              <div style={{ display:'flex', gap:12, padding:'12px 14px', alignItems:'flex-end', flexWrap:'wrap', borderBottom:`1px solid ${C.borderL}`, background:'#F8FAFC', animation:'slideDown 0.15s ease both' }}>
                {['From','To'].map((lbl,i) => (
                  <div key={lbl}>
                    <label style={{ display:'block', fontSize:11.5, fontWeight:600, color:C.t2, marginBottom:5 }}>{lbl}</label>
                    <DateInput value={i===0?filterFrom:filterTo} onChange={e=>i===0?setFilterFrom(e.target.value):setFilterTo(e.target.value)} style={{ height:36, padding:'0 32px 0 11px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:13, fontFamily:'inherit', outline:'none', color:C.t1, width:150 }}/>
                  </div>
                ))}
                <button onClick={() => { fetchLeads(1); setShowDatePanel(false); }} className="btn-navy" style={{ fontSize:13, padding:'8px 16px' }}>Apply</button>
                <button onClick={() => { setFilterFrom(''); setFilterTo(''); setShowDatePanel(false); }} className="btn-ghost" style={{ fontSize:13, padding:'8px 14px' }}>Reset</button>
              </div>
            )}
            <div style={{ display:'flex', gap:5, alignItems:'center', flexWrap:'wrap', padding:'8px 14px', background:'#FAFBFC' }}>
              <span style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.08em', marginRight:2 }}>Status:</span>
              {[{label:'All',color:C.navy,bg:C.navyL,border:C.navy},{label:'Follow Up',color:'#C2410C',bg:'#FFF7ED',border:'#FED7AA'},{label:'Login',color:'#6D28D9',bg:'#EDE9FE',border:'#C4B5FD'},{label:'Sanction',color:'#1D4ED8',bg:'#DBEAFE',border:'#BFDBFE'},{label:'Disbursement',color:'#15803D',bg:'#DCFCE7',border:'#BBF7D0'},{label:'PDD',color:'#0369A1',bg:'#E0F2FE',border:'#BAE6FD'},{label:'Revenue',color:'#0F766E',bg:'#CCFBF1',border:'#5EEAD4'},{label:'Completed',color:'#15803D',bg:'#DCFCE7',border:'#BBF7D0'},{label:'Closed',color:'#B91C1C',bg:'#FEF2F2',border:'#FECACA'}].map(f => {
                const isActive = filterStatus === f.label || (f.label === 'All' && !filterStatus);
                const sl = isMobile ? (f.label==='Disbursement'?'Disb.':f.label==='Follow Up'?'F/Up':f.label) : f.label;
                return <button key={f.label} onClick={() => { setFilterStatus(f.label==='All'?'':f.label); setActiveStatCard(null); setUrlPipeline(false); setSearchParams({}, { replace: true }); }} style={{ padding:'3px 10px', borderRadius:20, fontSize: isMobile ? 11 : 12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:`1.5px solid ${isActive ? f.border : C.border}`, background: isActive ? f.bg : '#fff', color: isActive ? f.color : C.t4 }}>{sl}</button>;
              })}
            </div>
          </div>

          {/* ACTIVE FILTER BADGE — shown when navigated from Dashboard */}
          {(activeStatCard && activeStatCard !== 'total') || urlPipeline ? (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', background:C.navyL, borderRadius:10, border:`1px solid ${C.navy}20`, animation:'slideDown 0.15s ease both' }}>
              <FiFilter size={12} style={{ color:C.navy }}/>
              <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>
                Filtered: {urlPipeline ? 'Pipeline' : statCards.find(c => c.key === activeStatCard)?.label || activeStatCard}
              </span>
              <button onClick={() => { setActiveStatCard(null); setUrlPipeline(false); setSearchParams({}, { replace: true }); }}
                style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:999, fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:'1.5px solid #FECACA', background:'#FEF2F2', color:'#DC2626' }}>
                <FiX size={10}/> Clear
              </button>
            </div>
          ) : null}

          {/* LOADING */}
          {loading && <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'16px', display:'flex', flexDirection:'column', gap:10 }}>{[1,2,3,4,5].map(i=><Shimmer key={i}/>)}</div>}

          {/* EMPTY */}
          {!loading && displayLeads.length === 0 && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, textAlign:'center', padding:'56px 24px' }}>
              <div style={{ width:64, height:64, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 18px' }}><FiUsers size={26} style={{ color:C.navy, opacity:0.55 }}/></div>
              <div style={{ fontSize:15, fontWeight:800, color:C.t1, marginBottom:7 }}>{hasFilters ? 'No leads match your filters' : 'No leads yet'}</div>
              <div style={{ fontSize:13.5, color:C.t4, marginBottom:20 }}>{hasFilters ? 'Try adjusting your search or filters' : 'Click "New Lead" to create the first lead'}</div>
              {hasFilters ? <button onClick={clearFilters} className="btn-ghost" style={{ fontSize:13 }}>Clear Filters</button> : <button onClick={() => navigate('/leads/create')} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'9px 20px', borderRadius:10, background:C.navy, color:'#fff', border:'none', fontWeight:700, fontSize:13.5, cursor:'pointer', fontFamily:'inherit' }}><FiPlus size={15}/> Create First Lead</button>}
            </div>
          )}

          {/* MOBILE CARDS */}
          {!loading && displayLeads.length > 0 && isMobile && (
            <>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {displayLeads.map(lead => <LeadCard key={lead.id} lead={lead} navigate={navigate} overdue={isOverdue(lead)} isToday={lead.next_follow_up_date===todayStr()} editOk={canEditLead(lead,userRole)} onEditClick={handleEditClick} userRole={userRole}/>)}
              </div>
              {showPagination && <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}` }}><Pagination currentPage={currentPage} totalCount={total} pageSize={PAGE_SIZE} onPageChange={handlePageChange}/></div>}
              <div style={{ textAlign:'center', fontSize:12.5, color:C.t4, padding:'4px 0 8px' }}>
                {clientFilterActive ? <span><strong style={{ color:C.t2 }}>{displayLeads.length}</strong> result{displayLeads.length!==1?'s':''} on this page (filtered)</span> : <span><strong style={{ color:C.t2 }}>{displayLeads.length}</strong> of <strong style={{ color:C.t2 }}>{total}</strong> leads</span>}
                {overdueCount > 0 && <span style={{ color:C.red, fontWeight:700, marginLeft:10 }}> · {overdueCount} overdue</span>}
              </div>
            </>
          )}

          {/* DESKTOP TABLE */}
          {!loading && displayLeads.length > 0 && !isMobile && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth: isTablet ? 750 : 960 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', borderBottom:`1.5px solid ${C.border}` }}>
                      {[{h:'Lead',a:'left'},{h:'User',a:'left',t:false},{h:'Customer',a:'left'},{h:'Product',a:'left',t:false},{h:'Amount',a:'right'},{h:'Source',a:'left',t:false},{h:'Status',a:'left'},{h:'Next Follow-up',a:'left'},{h:'Actions',a:'center'}].filter(c=>!isTablet||c.t!==false).map(({h,a})=>(
                        <th key={h} style={{ padding:'11px 14px', fontSize:10.5, fontWeight:800, color:'#1F2937', textTransform:'uppercase', letterSpacing:'0.08em', whiteSpace:'nowrap', textAlign:a }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayLeads.map((lead, idx) => {
                      const overdue = isOverdue(lead);
                      const isToday = lead.next_follow_up_date === todayStr();
                      const rowBg   = overdue ? 'rgba(220,38,38,0.018)' : C.white;
                      const isLast  = idx === displayLeads.length - 1;
                      const editOk  = canEditLead(lead, userRole);
                      return (
                        <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} style={{ background:rowBg, cursor:'pointer', transition:'background 0.07s', borderBottom: isLast ? 'none' : `1px solid ${C.borderL}` }} onMouseEnter={e=>e.currentTarget.style.background=overdue?'rgba(220,38,38,0.04)':'#F8FAFC'} onMouseLeave={e=>e.currentTarget.style.background=rowBg}>
                          <td style={{ padding:'12px 14px', verticalAlign:'middle', position:'relative' }}>
                            <div style={{ position:'absolute', left:0, top:10, bottom:10, width:3, borderRadius:'0 3px 3px 0', background: overdue ? '#DC2626' : isToday ? C.gold : 'transparent' }}/>
                            <div style={{ fontWeight:700, fontSize:12.5, color:C.navy, fontFamily:'monospace' }}>{lead.lead_no||`#${lead.id}`}</div>
                            <div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>{fmtDate(lead.lead_date)}</div>
                          </td>
                          {!isTablet && <td style={{ padding:'12px 14px', verticalAlign:'middle', fontSize:12.5, color:C.t2 }}>{lead.created_by_detail?.name || lead.created_by_detail?.first_name || '—'}</td>}
                          <td style={{ padding:'12px 14px', verticalAlign:'middle' }}>
                            <div style={{ fontWeight:700, fontSize:13.5, color:C.t1 }}>{lead.customer_name||'—'}</div>
                            {lead.mobile_number && <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:C.t3, marginTop:3 }}><FiPhone size={10}/>{lead.mobile_number}</div>}
                          </td>
                          {!isTablet && <td style={{ padding:'12px 14px', verticalAlign:'middle', fontSize:13, color:C.t2 }}>{lead.product?.name||'—'}</td>}
                          <td style={{ padding:'12px 14px', verticalAlign:'middle', textAlign:'right', fontSize:13.5, fontWeight:700, color:C.navy, whiteSpace:'nowrap' }}>{fmtAmount(lead.loan_amount)}</td>
                          {!isTablet && <td style={{ padding:'12px 14px', verticalAlign:'middle' }}><SourceBadge source={lead.lead_source}/></td>}
                          <td style={{ padding:'12px 14px', verticalAlign:'middle' }}><StatusBadge lead={lead}/></td>
                          <td style={{ padding:'12px 14px', verticalAlign:'middle' }}>
                            {lead.next_follow_up_date ? (
                              <div style={{ display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
                                {overdue ? <FiAlertCircle size={12} style={{ color:'#DC2626', flexShrink:0 }}/> : <FiCalendar size={11} style={{ color:C.t4, flexShrink:0 }}/>}
                                <span style={{ fontSize:13, fontWeight:overdue?700:500, color:overdue?'#DC2626':C.t1 }}>{fmtDate(lead.next_follow_up_date)}</span>
                                {overdue && <span style={{ fontSize:10, fontWeight:800, padding:'1px 7px', borderRadius:999, background:'#FEF2F2', color:'#DC2626', border:'1px solid #FECACA', flexShrink:0 }}>OVERDUE</span>}
                              </div>
                            ) : <span style={{ color:C.t4, fontSize:13 }}>—</span>}
                          </td>
                          <td style={{ padding:'12px 14px', verticalAlign:'middle', textAlign:'center' }} onClick={e=>e.stopPropagation()}>
                            <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                              <button title="View" onClick={e=>{e.stopPropagation();navigate(`/leads/${lead.id}`);}} style={{ width:28, height:28, borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.navy, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }} onMouseEnter={e=>{e.currentTarget.style.background=C.navyL;e.currentTarget.style.borderColor=C.navy;}} onMouseLeave={e=>{e.currentTarget.style.background=C.white;e.currentTarget.style.borderColor=C.border;}}><FiEye size={13}/></button>
                              <button title={editOk?'Edit':getEditBlockReason(lead,userRole)} onClick={e=>handleEditClick(e,lead)} style={{ width:28, height:28, borderRadius:7, border:`1px solid ${C.border}`, background: editOk?C.white:'#F3F4F6', color: editOk?C.t2:C.t4, cursor: editOk?'pointer':'not-allowed', display:'inline-flex', alignItems:'center', justifyContent:'center', opacity: editOk?1:0.6 }} onMouseEnter={e=>{if(editOk){e.currentTarget.style.background=C.navyL;e.currentTarget.style.borderColor=C.navy;}}} onMouseLeave={e=>{if(editOk){e.currentTarget.style.background=C.white;e.currentTarget.style.borderColor=C.border;}}}>{editOk?<FiEdit2 size={13}/>:<FiLock size={12}/>}</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {showPagination && <Pagination currentPage={currentPage} totalCount={total} pageSize={PAGE_SIZE} onPageChange={handlePageChange}/>}
              <div style={{ padding:'10px 18px', borderTop:`1px solid ${C.borderL}`, fontSize:12.5, color:C.t4, background:'#FAFBFC', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                {clientFilterActive
                  ? <span><strong style={{ color:C.t2 }}>{displayLeads.length}</strong> result{displayLeads.length!==1?'s':''} on this page <span style={{ color:C.navy, fontWeight:600 }}>(filtered)</span></span>
                  : <span>Showing <strong style={{ color:C.t2 }}>{displayLeads.length}</strong> of <strong style={{ color:C.t2 }}>{total}</strong> lead{total!==1?'s':''}{(filterSource||filterFrom||filterTo)&&<span style={{ color:C.navy, fontWeight:600 }}> (filtered)</span>}</span>
                }
                {overdueCount > 0 && <span style={{ color:'#DC2626', fontWeight:700, fontSize:12, display:'flex', alignItems:'center', gap:4 }}><FiAlertCircle size={11}/> {overdueCount} overdue</span>}
              </div>
            </div>
          )}
        </div>

        {editBlockModal && (
          <div style={{ position:'fixed', inset:0, zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.50)', backdropFilter:'blur(4px)' }} onClick={()=>setEditBlockModal(null)}>
            <div style={{ background:C.white, width:'100%', maxWidth:420, borderRadius:16, padding:'28px 28px 24px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', animation:'slideDown 0.18s ease both' }} onClick={e=>e.stopPropagation()}>
              <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:18 }}>
                <div style={{ width:46, height:46, borderRadius:'50%', background:'#EDE9FE', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><FiLock size={20} style={{ color:'#6D28D9' }}/></div>
                <div><h3 style={{ margin:0, fontSize:16, fontWeight:800, color:C.t1 }}>Edit Not Allowed</h3><p style={{ margin:'3px 0 0', fontSize:12.5, color:C.t4 }}>{editBlockModal.lead.customer_name}</p></div>
              </div>
              <div style={{ display:'flex', alignItems:'flex-start', gap:9, background:'#EDE9FE', borderRadius:10, padding:'12px 14px', marginBottom:20, border:'1px solid #C4B5FD' }}>
                <FiAlertCircle size={15} style={{ color:'#6D28D9', flexShrink:0, marginTop:1 }}/>
                <div style={{ fontSize:13, color:'#5B21B6', lineHeight:1.6 }}>{editBlockModal.reason}</div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end' }}><button onClick={()=>setEditBlockModal(null)} className="btn-navy" style={{ fontSize:13.5 }}>OK</button></div>
            </div>
          </div>
        )}
      </Layout>
    </>
  );
}

